import { OPEN_TASK_STATUSES } from './taskHelpers'
import type { TaskSearchRequest } from '@/composables/services/useTasksApi'

const OPEN_STATUS_FILTER = { op: 'in', value: OPEN_TASK_STATUSES }

function endOfTodayIso(): string {
  const d = new Date()
  d.setHours(23, 59, 59, 999)
  return d.toISOString()
}

export interface TaskViewContext {
  userId: string
  orgId: string
  teamIds: string[]
}

/**
 * Builds the fallback filter request for team tasks assigned via the legacy
 * links.owner_team_id pattern (owner_team_id embedded in the case link, with
 * owner_user_id explicitly null to exclude tasks already assigned to a user).
 * Run this alongside buildTaskViewRequest('team-tasks') and merge results to
 * cover both assignment styles until the API is normalised.
 */
export function buildTeamTasksFallbackRequest(
  ctx: TaskViewContext,
  pagination: { limit: number; start: number },
): TaskSearchRequest {
  return {
    sort: 'dueDate_asc',
    pagination,
    filters: {
      'links.owner_team_id': { op: 'in', value: ctx.teamIds },
      'links.owner_user_id': { op: 'eq', value: null },
      status: OPEN_STATUS_FILTER,
    },
  }
}

/**
 * Builds the fallback filter request for tasks assigned via the legacy
 * links.owner_user_id pattern (owner_user_id embedded in the case link).
 * Run this alongside buildTaskViewRequest and merge results to cover both
 * assignment styles until the API is normalised.
 */
export function buildOwnerUserIdFallbackRequest(
  viewId: 'my-tasks' | 'my-tasks-due-today',
  ctx: TaskViewContext,
  pagination: { limit: number; start: number },
): TaskSearchRequest {
  const common = { sort: 'dueDate_asc', pagination }
  switch (viewId) {
    case 'my-tasks-due-today':
      return {
        ...common,
        filters: {
          'links.owner_user_id': ctx.userId,
          status: OPEN_STATUS_FILTER,
          dueDate: { op: 'le', value: endOfTodayIso() },
        },
      }
    case 'my-tasks':
      return {
        ...common,
        filters: {
          'links.owner_user_id': ctx.userId,
          status: OPEN_STATUS_FILTER,
        },
      }
  }
}

export function buildTaskViewRequest(
  viewId: 'my-tasks' | 'my-tasks-due-today' | 'team-tasks' | 'all-tasks',
  ctx: TaskViewContext,
  pagination: { limit: number; start: number },
): TaskSearchRequest {
  const common = { sort: 'dueDate_asc', pagination }

  switch (viewId) {
    case 'my-tasks-due-today':
      return {
        ...common,
        filters: {
          'links.rel': 'user',
          'links.id': ctx.userId,
          status: OPEN_STATUS_FILTER,
          dueDate: { op: 'le', value: endOfTodayIso() },
        },
      }
    case 'my-tasks':
      return {
        ...common,
        filters: {
          'links.rel': 'user',
          'links.id': ctx.userId,
          status: OPEN_STATUS_FILTER,
        },
      }
    case 'team-tasks':
      return {
        ...common,
        filters: {
          'links.rel': 'team',
          'links.id': { op: 'in', value: ctx.teamIds },
          status: OPEN_STATUS_FILTER,
        },
      }
    case 'all-tasks':
      return {
        ...common,
        filters: {
          orgId: ctx.orgId,
          status: OPEN_STATUS_FILTER,
        },
      }
  }
}
